import React from "react";
import "./MicroplateFocus.scss";
import { WellPosition } from "../../Content";
import { LottiePlayer } from "@learningblocks/shared-components";
import { default as wells_a1_filled } from "./assets/wells_a1_filled.json";
import { default as wells_b1_filled } from "./assets/wells_b1_filled.json";
import { default as wells_a1_empty } from "./assets/wells_a1_empty.json";
import A1A3HalfFilledSvg from "./assets/a1-a3-half-filled.svg";
import A1A3FullFilledSvg from "./assets/a1-a3-full-filled.svg";
import B1BFullFilledSvg from "./assets/b1-b3-full-filled.svg";
import SimManager from "@learningblocks/simulation-engine/lib/managers/simManager";

interface MicroplateFocusProps {
  autoPlay: boolean;
  dispensibleWells: WellPosition[];
  playAnimation?: boolean;
  dispensibleSolution?: string;
}

const MicroplateFocus: React.FC<MicroplateFocusProps> = ({
  autoPlay,
  dispensibleWells,
  dispensibleSolution = "",
  playAnimation,
}) => {
  const lottieRef = React.useRef<any>();
  const previousWells = React.useRef<WellPosition[]>(dispensibleWells);

  // Reset animation when dispensible wells change
  React.useEffect(() => {
    if (
      lottieRef.current &&
      JSON.stringify(previousWells.current) !== JSON.stringify(dispensibleWells)
    ) {
      lottieRef.current.onClickStop();
      previousWells.current = dispensibleWells;
    }
  }, [dispensibleWells]);

  // Play animation when playAnimation prop changes
  React.useEffect(() => {
    if (playAnimation && lottieRef.current) {
      lottieRef.current.onClickStop();
      setTimeout(() => {
        if (lottieRef.current) {
          lottieRef.current.onClickPlay();
        }
      }, 0);
    }
  }, [playAnimation]);

  const autoPlayHandler = () => {
    if (lottieRef.current && autoPlay) lottieRef.current.onClickPlay();
  };

  const getWellsAnimation = () => {
    if (dispensibleWells[0] === "A1" && dispensibleSolution === "substrate") {
      return wells_a1_empty;
    } else if (dispensibleWells[0] === "B1") {
      return wells_b1_filled;
    } else {
      return wells_a1_filled;
    }
  };

  const getWellsImage = () => {
    const animation = getWellsAnimation();
    const DISPENSE_INTO_WELLS_DONE =
      SimManager.getInstance().getInputValue(
        "sim_state"
      ).DISPENSE_INTO_WELLS_DONE;
    const DISPENSE_INTO_WELLS_A2_A3_DONE =
      SimManager.getInstance().getInputValue(
        "sim_state"
      ).DISPENSE_INTO_WELLS_A2_A3_DONE;
    const DISPENSE_INTO_WELLS_B2_B3_DONE =
      SimManager.getInstance().getInputValue(
        "sim_state"
      ).DISPENSE_INTO_WELLS_B2_B3_DONE;

    if (animation === wells_b1_filled && !DISPENSE_INTO_WELLS_A2_A3_DONE) {
      return false;
    }
    if (animation === wells_a1_empty && DISPENSE_INTO_WELLS_DONE) {
      return A1A3HalfFilledSvg;
    }
    if (animation === wells_a1_filled && DISPENSE_INTO_WELLS_A2_A3_DONE) {
      return A1A3FullFilledSvg;
    }
    if (animation === wells_a1_filled && !DISPENSE_INTO_WELLS_B2_B3_DONE) {
      return false;
    }
    if (animation === wells_b1_filled && DISPENSE_INTO_WELLS_B2_B3_DONE) {
      return B1BFullFilledSvg;
    }
    return false;
  };

  return (
    <div className="microplate-focus-wrapper">
      {getWellsImage() ? (
        <img
          src={getWellsImage() as string}
          className={"microplate-focus-image"}
        />
      ) : (
        <LottiePlayer
          ref={(ref: any) => (lottieRef.current = ref)}
          autoplay={autoPlay}
          showContorls={false}
          animationData={getWellsAnimation()}
          animationLoaded={autoPlayHandler}
          className={"microplate-focus-animation"}
          stopOnLastFrame={true}
        />
      )}
    </div>
  );
};

export default MicroplateFocus;
